import React, { useState } from 'react';

interface FAQItem {
  q: string;
  a: string;
  category: string;
}

const FAQ_DATA: FAQItem[] = [
  // Allgemeines
  {
    category: "Allgemeines",
    q: "Was ist ErsteHilfe2Go?",
    a: "ErsteHilfe2Go ist eine digitale Erste-Hilfe-Plattform, die Menschen im Notfall dabei unterstützt, ruhig und richtig zu handeln. Die App bietet verständliche Schritt-für-Schritt-Anleitungen für typische Notfallsituationen – jederzeit und auch offline."
  },
  {
    category: "Allgemeines",
    q: "Ist die Nutzung kostenlos?",
    a: "Ja! Zum Start wird es eine kostenfreie Basisversion geben, die alle lebensrettenden Kernfunktionen enthält. Wir finanzieren uns über freiwillige Premium-Inhalte und starke Partner, um die App für alle zugänglich zu halten."
  },
  {
    category: "Allgemeines",
    q: "Benötige ich eine Internetverbindung?",
    a: "Der Notfall-Guide und alle Lektionen funktionieren komplett offline. So bist du auch im tiefsten Wald oder im Funkloch sicher und hast deine Erste Hilfe Anleitung immer dabei. Zum Anmelden beim ersten Start und speichern deines Fortschritts in der Cloud wird eine Internetverbindung benötigt."
  },
  {
    category: "Allgemeines",
    q: "Für wen ist ErsteHilfe2Go gedacht?",
    a: "Für alle! Egal ob Fahrschüler, Eltern, Senioren oder Ersthelfer im Betrieb – unsere App ist so gestaltet, dass jeder, unabhängig vom Vorwissen, zum Lebensretter werden kann."
  },
  
  // Notfall & Sicherheit
  {
    category: "Notfall & Sicherheit",
    q: "Ersetzt die App einen Kurs oder den Notruf?",
    a: "Nein. ErsteHilfe2Go ersetzt weder den gesetzlich vorgeschriebenen Erste-Hilfe-Kurs (z.B. für den Führerschein) noch den Notruf (112). Die App dient als digitaler Begleiter zur Auffrischung und als Gedächtnisstütze im Stressmoment."
  },
  {
    category: "Notfall & Sicherheit",
    q: "Sind die Inhalte medizinisch geprüft?",
    a: "Absolut. Alle Inhalte werden in enger Zusammenarbeit mit Notfallsanitätern und Medizinern erstellt und basieren auf den aktuellsten Leitlinien des European Resuscitation Council (ERC). Mehr dazu erfährst du auf unserer Seite zur Medizinischen Transparenz."
  },
  {
    category: "Notfall & Sicherheit",
    q: "Was passiert im echten Notfall?",
    a: "In lebensbedrohlichen Situationen weist die App dich sofort darauf hin, den Notruf (112) zu wählen. Danach führt dich unser Notfall-Modus mit klaren Sprachansagen durch die ersten Maßnahmen bis zum Eintreffen der Profis."
  },
  {
    category: "Notfall & Sicherheit",
    q: "Wie aktuell sind die Inhalte?",
    a: "Wir überwachen medizinische Standards kontinuierlich. Sobald sich offizielle Leitlinien ändern, aktualisieren wir unsere digitalen Module innerhalb weniger Tage."
  },

  // Recht & Datenschutz
  {
    category: "Recht & Datenschutz",
    q: "Übernehmt ihr eine Haftung für die Inhalte?",
    a: "Die Nutzung erfolgt auf eigene Verantwortung. Als Ersthelfer bist du rechtlich durch das Gesetz geschützt. Unsere App gibt dir die Sicherheit, diesen Schutz durch korrektes Handeln voll auszuschöpfen, indem sie dir eine validierte Erste Hilfe Anleitung bietet."
  },
  {
    category: "Recht & Datenschutz",
    q: "Welche Daten werden gespeichert?",
    a: "Wir erheben nur absolut notwendige Daten für den Betrieb. Deine Lernfortschritte werden lokal oder verschlüsselt auf deutschen Servern gespeichert. Wir verkaufen niemals Daten an Dritte."
  },
  {
    category: "Recht & Datenschutz",
    q: "Gibt es Tracking oder Werbung?",
    a: "Nein. In einer Notfall-App hat Werbung nichts zu suchen. Wir verzichten auf nervige Banner und invasives Tracking, um deine Privatsphäre und Konzentration zu schützen."
  },

  // Entwicklung & Pre-Launch
  {
    category: "Entwicklung",
    q: "Wann erscheint die App?",
    a: "ErsteHilfe2Go befindet sich aktuell in der finalen Entwicklungsphase. Wir planen den Rollout für das Jahr 2026. Melde dich auf der Warteliste an, um den genauen Termin zu erfahren!"
  },
  {
    category: "Entwicklung",
    q: "Kann ich die App international nutzen?",
    a: "Der Notfall-Guide basiert auf europäischen Standards (ERC). Während die App primär für den deutschen Markt optimiert ist, sind die lebensrettenden Maßnahmen weltweit nach diesen Standards anwendbar."
  }
];

interface FAQProps {
  showCategories?: boolean;
  limit?: number;
}

const FAQ: React.FC<FAQProps> = ({ showCategories = true, limit }) => {
  const [activeCategory, setActiveCategory] = useState<string>("Allgemeines");
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const categories = Array.from(new Set(FAQ_DATA.map(item => item.category)));
  
  const filteredData = showCategories 
    ? FAQ_DATA.filter(item => item.category === activeCategory)
    : FAQ_DATA.slice(0, limit || 4);

  return (
    <div className="container mx-auto px-4 md:px-6">
      {showCategories && (
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => {
                setActiveCategory(cat);
                setOpenIndex(0);
              }}
              className={`px-6 py-3 rounded-full text-sm font-bold transition-all border-2 ${
                activeCategory === cat 
                ? 'bg-primary border-primary text-white shadow-lg' 
                : 'bg-white border-gray-100 text-gray-400 hover:border-primary/20 hover:text-secondary'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      <div className="max-w-3xl mx-auto space-y-4">
        {filteredData.map((f, i) => (
          <div 
            key={i} 
            className={`bg-white rounded-3xl border-2 transition-all duration-300 ${
              openIndex === i ? 'border-primary/20 shadow-xl' : 'border-gray-100 shadow-sm hover:border-gray-200'
            }`}
          >
            <button 
              className="w-full text-left px-8 py-6 flex items-center justify-between font-bold text-secondary text-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset rounded-3xl"
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
              aria-expanded={openIndex === i}
              aria-controls={`faq-answer-${activeCategory}-${i}`}
            >
              <span className="pr-8">{f.q}</span>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${openIndex === i ? 'bg-primary text-white rotate-180' : 'bg-gray-50 text-gray-400'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>
            <div 
              id={`faq-answer-${activeCategory}-${i}`}
              className={`transition-all duration-300 ease-in-out overflow-hidden ${
                openIndex === i ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="px-8 pb-8 text-gray-500 leading-relaxed text-base">
                <div className="pt-2 border-t border-gray-50 mt-2">
                  {f.a}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {!showCategories && (
        <div className="mt-12 text-center">
          <p className="text-gray-400 text-sm mb-4">Noch mehr Fragen?</p>
          <button 
            onClick={() => window.location.hash = 'faq'}
            className="text-primary font-bold hover:underline underline-offset-4"
          >
            Alle FAQs anzeigen →
          </button>
        </div>
      )}
    </div>
  );
};

export default FAQ;